import React, { useState, useCallback } from 'react';
import { X, Download, FileImage, FileText, Share2, ExternalLink, BookOpen, MessageCircle, Smartphone } from 'lucide-react';
import { POSTERS } from '../../config/posters';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';
import { tSurf } from '../../config/newSurfacesTranslations';


/**
 * KnowledgePosters — teaching-first, share-ready poster gallery.
 *
 * Lives at the bottom of the home page (`ZakatGlobalPage.js`). Renders a
 * 3-up grid of poster tiles. Clicking a live tile opens a full-view modal
 * with two primary actions:
 *   • Download PNG — direct file save (kept as-supplied, high-res)
 *   • Download PDF — wraps the PNG in a single-page A-portrait PDF via
 *     jsPDF (lazy-imported so the ~200KB bundle only ships on demand).
 *
 * Coming-soon tiles are shown as elegant teasers so users see the series
 * has depth without breaking the grid rhythm.
 *
 * Zero backend, zero tracking — this is an editorial content surface only.
 */
const THEME_ACCENT = {
  wealth: { ribbon: 'bg-[#EDF5F1] text-[#0D4733]', dot: 'bg-[#EDF5F1]', pill: 'text-[#4A5568] border-[#E2DCD5]' },
  spirit: { ribbon: 'bg-[#0D4733] text-gold-200',    dot: 'bg-[#0D4733]',    pill: 'text-[#0D1520] border-[#CBD2D9]' },
  family: { ribbon: 'bg-rose-900 text-gold-200',    dot: 'bg-rose-700',    pill: 'text-rose-800 border-rose-200' },
};

// Trigger a client-side file download from a URL. Uses a hidden anchor so
// the browser saves rather than navigates.
const saveAsFile = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 200);
};

// Wrap a PNG at `url` into a single-page A-portrait PDF sized so the image
// fills the width with 24pt margin. jsPDF is loaded lazily to keep the
// initial home-page JS bundle small.
const downloadAsPdf = async (url, filename) => {
  const [{ jsPDF }, imgDims] = await Promise.all([
    import('jspdf'),
    new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight, img });
      img.onerror = reject;
      img.src = url;
    }),
  ]);
  const A_W = 595.28; // A4 width in pt
  const margin = 24;
  const imgW = A_W - margin * 2;
  const imgH = imgW * (imgDims.h / imgDims.w);
  const pageH = imgH + margin * 2;
  const pdf = new jsPDF({ unit: 'pt', format: [A_W, pageH], compress: true });
  // Draw via canvas so PDF is guaranteed to embed
  const canvas = document.createElement('canvas');
  canvas.width = imgDims.w;
  canvas.height = imgDims.h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imgDims.img, 0, 0);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  pdf.addImage(dataUrl, 'JPEG', margin, margin, imgW, imgH, undefined, 'FAST');
  pdf.save(filename);
};

// A single tile in the responsive gallery. Supports two visual scales via
// the `featured` prop — a wider spotlight tile at the top of the grid
// (used for the newest poster) and the standard 3-up grid tile.
const PosterTile = ({ poster, onOpen, testid, ts, featured = false, isNew = false }) => {
  const isLive = poster.status === 'live';
  const accent = THEME_ACCENT[poster.theme] || THEME_ACCENT.wealth;
  return (
    <button
      type="button"
      onClick={isLive ? () => onOpen(poster) : undefined}
      disabled={!isLive}
      data-testid={testid}
      aria-label={isLive ? `Open poster: ${poster.title}` : `${poster.title} — coming soon`}
      className={`group relative text-start rounded-2xl overflow-hidden border transition-all ${
        isLive
          ? 'border-stone-200 bg-white hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(6,49,36,0.35)] hover:border-gold-500/60 cursor-pointer'
          : 'border-stone-200 bg-stone-50/60 cursor-not-allowed'
      } ${featured ? 'lg:col-span-2' : ''}`}
    >
      {/* NEW badge — sits on top of the ribbon */}
      {isNew && isLive && (
        <span
          data-testid={`${testid}-new`}
          className="absolute top-3 end-3 z-[2] inline-flex items-center gap-1 rounded-full bg-gold-500 text-[#0D1520] text-[10px] font-mono uppercase tracking-[0.14em] font-bold px-2.5 py-1 shadow-md ring-2 ring-white"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#EDF5F1] animate-pulse" aria-hidden />
          New
        </span>
      )}

      {/* Ribbon */}
      <div className={`${accent.ribbon} px-3.5 py-1.5 flex items-center justify-between gap-2`}>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold truncate">
          {poster.series}: {String(poster.seriesNum).padStart(2, '0')}
        </span>
        <span className="text-[9.5px] font-mono uppercase tracking-[0.14em] opacity-70">
          {poster.kicker}
        </span>
      </div>

      {/* Preview area — all tiles share the same 3/4 aspect regardless of
          feature status so the grid rows stay equal-height. The featured
          tile is wider (col-span-2) but not taller, eliminating the
          whitespace previously caused by row-span-2 + shorter neighbours. */}
      <div className="relative aspect-[3/4] bg-stone-100 overflow-hidden">
        {isLive ? (
          <img
            src={poster.thumb || poster.image}
            alt={`${poster.title} — ${poster.subtitle}. ${poster.excerpt}`}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          // Coming-soon skeleton
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4 text-center">
            <div className={`w-14 h-14 rounded-full ${accent.dot} opacity-15 flex items-center justify-center`}>
              <BookOpen className="w-6 h-6 text-[#0D1520]" />
            </div>
            <p className="font-heading text-[#0D1520] text-lg leading-tight px-2">{poster.title}</p>
            <p className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-stone-500">
              {ts('posters.comingSoon')}
            </p>
          </div>
        )}
        {isLive && (
          <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-[#EDF5F1] via-[#EDF5F1] to-transparent flex items-end justify-center p-4">
            <span className="text-[#0D1520] text-[12.5px] font-semibold inline-flex items-center gap-1.5 rounded-full bg-[#EDF5F1] backdrop-blur px-3 py-1.5 border border-gold-500/40">
              {ts('posters.readFull')} <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className={`space-y-2 ${featured ? 'p-5 sm:p-6' : 'p-4'}`}>
        <h3 className={`font-heading text-[#0D1520] leading-tight ${featured ? 'text-xl sm:text-2xl' : 'text-lg'}`}>{poster.title}</h3>
        <p className={`text-stone-500 line-clamp-2 leading-snug ${featured ? 'text-[13px]' : 'text-[12px]'}`}>{poster.excerpt}</p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {poster.tags?.slice(0, 3).map((t) => (
            <span
              key={t}
              className={`inline-flex items-center rounded-full border ${accent.pill} bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide`}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
};

// Full-view modal — image on the left, meta + downloads on the right.
// Exported so external surfaces (Learn page, Will page thumb-strip) can
// reuse the same viewer via `PosterThumbStrip`.
export const PosterModal = ({ poster, onClose, ts }) => {
  const [busy, setBusy] = useState(null); // 'png' | 'pdf' | null
  const baseName = `amanah-${poster.id}`;

  const doPng = useCallback(() => {
    setBusy('png');
    try {
      saveAsFile(poster.image, `${baseName}.png`);
    } finally {
      setTimeout(() => setBusy(null), 600);
    }
  }, [poster, baseName]);

  const doPdf = useCallback(async () => {
    setBusy('pdf');
    try {
      await downloadAsPdf(poster.image, `${baseName}.pdf`);
    } catch (e) {
      // Fallback to PNG so the user still gets a file.
      saveAsFile(poster.image, `${baseName}.png`);
    } finally {
      setBusy(null);
    }
  }, [poster, baseName]);

  const doShare = useCallback(async () => {
    const shareUrl = `${window.location.origin}${poster.image}`;
    const text = `${poster.title} — ${poster.subtitle}\n\n"${poster.excerpt}"\n\nvia Amanah by Zakat.International`;
    if (navigator.share) {
      try {
        await navigator.share({ title: poster.title, text, url: shareUrl });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
    } catch { /* ignore */ }
  }, [poster]);

  // Deep-link to WhatsApp with a pre-filled dawah-friendly caption. Every
  // share is a subtle backlink to zakat.international, so the poster spreads
  // organically without any tracking or accounts.
  const doWhatsApp = useCallback(() => {
    const homepage = window.location.origin;
    const caption =
      `${poster.title}\n` +
      `"${poster.excerpt}"\n\n` +
      `Learn your rights & duties from the Qur'an — free, private, in 15 languages.\n` +
      `${homepage}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(caption)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  }, [poster]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Poster viewer: ${poster.title}`}
      data-testid="poster-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-[#EDF5F1] backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl max-h-[92vh] rounded-2xl overflow-hidden bg-white shadow-[0_40px_120px_-24px_rgba(0,0,0,0.6)] grid grid-cols-1 lg:grid-cols-12"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          data-testid="poster-modal-close"
          aria-label={ts('posters.close')}
          className="absolute top-3 end-3 z-10 inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#F3EFEA] hover:bg-[#FAF8F5] text-stone-700 hover:text-[#0D1520] shadow-lg backdrop-blur transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* LEFT — poster image */}
        <div className="lg:col-span-8 bg-stone-100 flex items-center justify-center overflow-hidden pb-16 lg:pb-0">
          <img
            src={poster.image}
            alt={`${poster.title} — ${poster.subtitle}. ${poster.excerpt}`}
            data-testid="poster-modal-image"
            className="max-h-[92vh] w-auto max-w-full object-contain"
          />
        </div>

        {/* Mobile-only sticky action bar — visible on top of the image on
            small screens. Two big touch targets: Save to phone (PNG) +
            Share to WhatsApp with dawah-friendly caption. Every share is a
            subtle backlink to zakat.international. */}
        <div
          data-testid="poster-mobile-bar"
          className="lg:hidden absolute bottom-0 inset-x-0 z-10 flex items-center gap-2 px-3 py-2.5 bg-gradient-to-t from-[#EDF5F1] via-[#EDF5F1] to-[#EDF5F1] backdrop-blur-sm border-t border-gold-500/30"
        >
          <button
            type="button"
            onClick={doPng}
            data-testid="poster-mobile-save"
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[#F3EFEA] hover:bg-[#FAF8F5] text-[#0D1520] font-semibold text-[13px] shadow-md active:scale-[0.98] transition-all"
          >
            <Smartphone className="w-4 h-4" />
            {ts('posters.mobileSave')}
          </button>
          <button
            type="button"
            onClick={doWhatsApp}
            data-testid="poster-mobile-whatsapp"
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-[#0D1520] font-semibold text-[13px] shadow-md active:scale-[0.98] transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            {ts('posters.mobileWa')}
          </button>
        </div>

        {/* RIGHT — meta + actions */}
        <aside className="lg:col-span-4 p-5 sm:p-7 overflow-y-auto max-h-[92vh] bg-gradient-to-b from-[#FAF8F5] to-stone-50">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#0D4733] font-semibold">
            {poster.series} · {String(poster.seriesNum).padStart(2, '0')}
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-[#0D1520] leading-tight">
            {poster.title}
          </h2>
          <p className="mt-1.5 text-[12.5px] text-stone-500 italic">{poster.subtitle}</p>

          <div className="mt-5 rounded-xl bg-gold-50/60 border border-gold-300/60 p-4">
            <p className="text-[13.5px] text-stone-800 leading-relaxed italic">
              &ldquo;{poster.excerpt}&rdquo;
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {poster.tags?.map((t) => (
              <span key={t} className="inline-flex items-center rounded-full border border-[#E2DCD5] bg-[#F3EFEA] text-[#4A5568] px-2.5 py-0.5 text-[10.5px] font-mono uppercase tracking-wide">
                {t}
              </span>
            ))}
          </div>

          {/* Download actions */}
          <div className="mt-6 space-y-2">
            <button
              type="button"
              onClick={doPng}
              disabled={busy === 'png'}
              data-testid="poster-download-png"
              className="w-full inline-flex items-center justify-between gap-2 h-11 px-4 rounded-xl bg-[#EDF5F1] hover:bg-[#EDF5F1] text-[#0D1520] font-semibold text-[13px] transition-colors disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <FileImage className="w-4 h-4" />
                {busy === 'png' ? ts('posters.dlPngBusy') : ts('posters.dlPng')}
              </span>
              <Download className="w-4 h-4 opacity-80" />
            </button>
            <button
              type="button"
              onClick={doPdf}
              disabled={busy === 'pdf'}
              data-testid="poster-download-pdf"
              className="w-full inline-flex items-center justify-between gap-2 h-11 px-4 rounded-xl border-2 border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA] font-semibold text-[13px] transition-colors disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {busy === 'pdf' ? ts('posters.dlPdfBusy') : ts('posters.dlPdf')}
              </span>
              <Download className="w-4 h-4 opacity-80" />
            </button>
            <button
              type="button"
              onClick={doShare}
              data-testid="poster-share"
              className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border border-stone-300 text-stone-700 hover:text-[#0D1520] hover:border-[#0D4733] font-medium text-[12.5px] transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              {ts('posters.share')}
            </button>
            <button
              type="button"
              onClick={doWhatsApp}
              data-testid="poster-whatsapp"
              className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-[#25D366] hover:bg-[#1EBE5C] text-[#0D1520] font-semibold text-[12.5px] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {ts('posters.shareWa')}
            </button>
          </div>

          {/* Source */}
          <p className="mt-6 pt-5 border-t border-stone-200 text-[11px] text-stone-500 leading-relaxed">
            {poster.source}
          </p>
        </aside>
      </div>
    </div>
  );
};

export const KnowledgePosters = () => {
  const [active, setActive] = useState(null);
  const { selectedLanguage } = useZakatGlobal();
  const lang = selectedLanguage?.code || 'en';
  const ts = (path) => tSurf(lang, path);

  // Lock body scroll while modal is open
  React.useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);

  // Close on Escape
  React.useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  // Sort newest first (highest seriesNum) so the featured tile is always
  // the freshest scholar addition. Mark the top 2 as NEW to draw the eye
  // to the latest editorial drops.
  const sortedPosters = [...POSTERS].sort((a, b) => (b.seriesNum || 0) - (a.seriesNum || 0));
  const liveCount = sortedPosters.filter((p) => p.status === 'live').length;
  const newIds = new Set(sortedPosters.filter((p) => p.status === 'live').slice(0, 2).map((p) => p.id));

  return (
    <section
      id="knowledge-posters"
      data-testid="knowledge-posters"
      className="relative py-14 sm:py-20 bg-gradient-to-b from-[#FAF8F5] via-stone-50/60 to-[#F3EFEA] border-t border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section head */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#0D4733] font-semibold">
              {ts('posters.kicker')}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0D1520] leading-[1.1] tracking-tight">
              {ts('posters.title')}
            </h2>
            <p className="mt-3 text-[14.5px] text-stone-600 leading-relaxed">
              {ts('posters.subtitle')}
            </p>
          </div>
          <div
            data-testid="posters-counter"
            className="flex items-center gap-2 shrink-0"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E2DCD5] bg-[#F3EFEA] px-3 py-1.5 text-[#0D1520]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0D4733] animate-pulse" aria-hidden />
              <span className="text-[11.5px] font-mono uppercase tracking-[0.14em] font-semibold">{liveCount} posters</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold-300 bg-gold-50 px-3 py-1.5 text-gold-800">
              <BookOpen className="w-3.5 h-3.5" />
              <span className="text-[11.5px] font-mono uppercase tracking-[0.14em] font-semibold">Ibn Ashur series</span>
            </span>
          </div>
        </div>

        {/* Grid — newest tile is featured (wider col-span-2 on desktop
            only). `auto-rows-fr` keeps every row the same height so
            shorter cards stretch to fill instead of leaving whitespace
            underneath the tall featured card. */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-5 lg:gap-7">
          {sortedPosters.map((p, i) => (
            <PosterTile
              key={p.id}
              poster={p}
              onOpen={setActive}
              testid={`poster-tile-${i}`}
              ts={ts}
              featured={i === 0 && p.status === 'live'}
              isNew={newIds.has(p.id)}
            />
          ))}
        </div>

        {/* Attribution strip */}
        <p className="mt-8 text-[11.5px] text-stone-500 italic text-center max-w-3xl mx-auto">
          {ts('posters.attribution')}
        </p>
      </div>

      {active && <PosterModal poster={active} onClose={() => setActive(null)} ts={ts} />}
    </section>
  );
};

export default KnowledgePosters;
